'use client'

import React, { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Box, Drawer, Stack } from '@mantine/core'
import { useIsMobile } from '@/lib/mobileDetector'
import { useUserContext } from '@/contexts/UserContext'
import { RefreshCtaButton } from '@/components/Refresh/CategoryPages/RefreshCtaButton'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { MenuSection } from '@/types/interfaces/menu'
import { PageCategory } from '@/types/enums/StoreTypes'
import { DrawerHeader } from '@/components/NavBar/MenuDrawer/DrawerHeader'
import { DrillSection } from '@/components/NavBar/MenuDrawer/DrillSection'
import { TopLevelMenu } from '@/components/NavBar/MenuDrawer/TopLevelMenu'
const DRAWER_BG = 'var(--gm-bg-solid, #f5f1ed)'
const SLIDE_DURATION = 0.32
const SLIDE_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1]

const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
}

type MenuDrawerProps = {
    isLoggedIn: boolean | undefined
    showMenuDrawer: boolean
    setShowMenuDrawer: (open: boolean) => void
}

const trackMenuAction = (action: string, name?: string) =>
    trackStoreEvent(StoreAnalytics.NAV_HAMBURGER_MENU, { action, name })

const MenuDrawer: React.FC<MenuDrawerProps> = ({ isLoggedIn, showMenuDrawer, setShowMenuDrawer }) => {
    const { logoutUser } = useUserContext()
    const isMobile = useIsMobile()
    const [drillSection, setDrillSection] = useState<MenuSection | undefined>(undefined)
    const [direction, setDirection] = useState(0)

    useEffect(() => {
        if (!showMenuDrawer) {
            setDrillSection(undefined)
            setDirection(0)
        }
    }, [showMenuDrawer])

    const close = () => setShowMenuDrawer(false)

    const closeAndNavigate = (slug: string) => {
        close()
        trackMenuAction('navigate', slug)
    }

    const openDrillSection = (section: MenuSection, slug: string) => {
        setDirection(1)
        setDrillSection(section)
        trackMenuAction('open-section', slug)
    }

    const closeDrillSection = () => {
        if (!drillSection) return
        trackMenuAction('close-section', drillSection.title)
        setDirection(-1)
        setDrillSection(undefined)
    }

    const handleTopLevelClick = (e: React.MouseEvent, item: PageCategory) => {
        if (item.overrideSection) {
            e.preventDefault()
            openDrillSection(item.overrideSection, item.slug)
            return
        }
        closeAndNavigate(item.slug)
    }

    const handleLogoClick = () => {
        close()
        trackMenuAction('logo')
    }

    const handleSignOut = () => {
        close()
        trackMenuAction('logout')
        logoutUser()
    }

    const handleSignIn = () => {
        close()
        trackMenuAction('sign-in')
    }

    const sectionKey = drillSection?.title ?? 'top-level'
    const showSignIn = !drillSection && !isLoggedIn

    return (
        <Drawer
            opened={showMenuDrawer}
            onClose={close}
            position="right"
            size={isMobile ? '100%' : 440}
            withCloseButton={false}
            zIndex={1001}
            overlayProps={{ backgroundOpacity: 0.5, color: 'var(--gm-fg-primary-black, #1f1e1d)' }}
            transitionProps={{ transition: 'slide-left', duration: 350, timingFunction: 'ease-in-out' }}
            styles={{
                content: {
                    backgroundColor: DRAWER_BG,
                    borderTopLeftRadius: isMobile ? 0 : 24,
                    borderBottomLeftRadius: isMobile ? 0 : 24,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    display: 'flex',
                    flexDirection: 'column',
                },
                body: { padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
            }}
        >
            <DrawerHeader
                showBack={!!drillSection}
                direction={direction}
                onBack={closeDrillSection}
                onClose={close}
                onLogoClick={handleLogoClick}
            />

            <Box style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
                <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                        key={sectionKey}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: SLIDE_DURATION, ease: SLIDE_EASE }}
                        style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}
                    >
                        <Box style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            <Stack gap={48} px={24} py={20}>
                                {drillSection ? (
                                    <DrillSection
                                        section={drillSection}
                                        isLoggedIn={!!isLoggedIn}
                                        onItemClick={(item) => closeAndNavigate(item.slug)}
                                    />
                                ) : (
                                    <TopLevelMenu
                                        isLoggedIn={isLoggedIn}
                                        onItemClick={handleTopLevelClick}
                                        onAccountItemClick={(item) => closeAndNavigate(item.slug)}
                                        onSignOut={handleSignOut}
                                    />
                                )}
                            </Stack>
                        </Box>
                        {showSignIn && (
                            <Box px={24} py={20} style={{ flexShrink: 0, backgroundColor: DRAWER_BG }}>
                                <RefreshCtaButton variant="primary" href="/login" fullWidth onClick={handleSignIn}>
                                    Sign in
                                </RefreshCtaButton>
                            </Box>
                        )}
                    </motion.div>
                </AnimatePresence>
            </Box>
        </Drawer>
    )
}

export default MenuDrawer
